import { useCallback } from 'react';

import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  CreateInviteResult,
  TeamInvite,
  TeamInviteFailureReason,
  TeamInviteStatus,
  TeamRole,
} from '@this+that/models';

import { FETCH_POLICY_CACHE_FIRST } from '../utils/constants';

const TEAM_INVITE_FIELDS = gql`
  fragment TeamInviteFields on TeamInvite {
    inviteeEmail
    teamId
    teamName
    inviterUserId
    inviterUserName
    inviteeRole
    inviteStatus
    invitedAt
    respondedAt
    lastSentAt
    memberCount
  }
`;

interface GetUserTeamInvitesData {
  getUserTeamInvites: TeamInvite[];
}

interface GetTeamInvitesData {
  getTeamInvites: TeamInvite[];
}

interface CreateInviteData {
  createInvite: CreateInviteResult;
}

interface DeleteInviteData {
  deleteInvite: boolean;
}

interface AcceptInviteData {
  acceptInvite: boolean;
}

interface RejectInviteData {
  rejectInvite: boolean;
}

const GET_USER_TEAM_INVITES = gql`
  query getUserTeamInvites($status: String!, $includeMemberCount: Boolean) {
    getUserTeamInvites(status: $status, includeMemberCount: $includeMemberCount) {
      ...TeamInviteFields
    }
  }
  ${TEAM_INVITE_FIELDS}
`;

export function useHasPendingTeamInvites() {
  const { data, loading } = useQuery<GetUserTeamInvitesData>(
    GET_USER_TEAM_INVITES,
    {
      variables: {
        status: TeamInviteStatus.PENDING,
        includeMemberCount: true,
      },
      fetchPolicy: FETCH_POLICY_CACHE_FIRST,
    },
  );
  const hasPendingInvites = (data?.getUserTeamInvites?.length ?? 0) > 0;
  return { hasPendingInvites, loading };
}

export function useGetUserTeamInvites() {
  const { loading, error, refetch } = useQuery<GetUserTeamInvitesData>(
    GET_USER_TEAM_INVITES,
    {
      skip: true,
    },
  );

  const getUserTeamInvites = useCallback(
    async (status: TeamInviteStatus, includeMemberCount = false) => {
      const { data } = await refetch({ status, includeMemberCount });
      return data?.getUserTeamInvites;
    },
    [refetch],
  );

  return { getUserTeamInvites, loading, error };
}

const GET_TEAM_INVITES = gql`
  query getTeamInvites($teamId: String!) {
    getTeamInvites(teamId: $teamId) {
      ...TeamInviteFields
    }
  }
  ${TEAM_INVITE_FIELDS}
`;

export function useGetTeamInvites() {
  const { loading, error, refetch } = useQuery<GetTeamInvitesData>(
    GET_TEAM_INVITES,
    {
      skip: true,
    },
  );

  const getTeamInvites = useCallback(
    async (teamId: string) => {
      const { data } = await refetch({ teamId });
      return data?.getTeamInvites;
    },
    [refetch],
  );

  return { getTeamInvites, loading, error };
}

const CREATE_INVITE = gql`
  mutation createInvite(
    $teamId: String!
    $inviteeEmail: String!
    $inviteeRole: String
  ) {
    createInvite(
      teamId: $teamId
      inviteeEmail: $inviteeEmail
      inviteeRole: $inviteeRole
    ) {
      __typename
      ... on TeamInviteSucceeded {
        outcome
        invite {
          ...TeamInviteFields
        }
      }
      ... on TeamInviteFailed {
        reason
      }
    }
  }
  ${TEAM_INVITE_FIELDS}
`;

export function useCreateInvite() {
  const [createInviteMutation, { loading, error }] =
    useMutation<CreateInviteData>(CREATE_INVITE);

  const createInvite = useCallback(
    async (teamId: string, inviteeEmail: string, inviteeRole?: TeamRole) => {
      const { data } = await createInviteMutation({
        variables: {
          teamId,
          inviteeEmail,
          inviteeRole,
        },
      });
      // errorPolicy is 'all', so a resolver failure yields data === undefined
      // alongside an error. Collapse it to the same shape the union uses so
      // callers have one branch to handle, not two.
      return (
        data?.createInvite ?? {
          __typename: 'TeamInviteFailed' as const,
          reason: TeamInviteFailureReason.UNKNOWN,
        }
      );
    },
    [createInviteMutation],
  );

  return { createInvite, loading, error };
}

const DELETE_INVITE = gql`
  mutation deleteInvite($teamId: String!, $inviteeEmail: String!) {
    deleteInvite(teamId: $teamId, inviteeEmail: $inviteeEmail)
  }
`;

export function useDeleteInvite() {
  const [deleteInviteMutation, { loading, error }] =
    useMutation<DeleteInviteData>(DELETE_INVITE);

  const deleteInvite = useCallback(
    async (teamId: string, inviteeEmail: string) => {
      const { data } = await deleteInviteMutation({
        variables: { teamId, inviteeEmail },
      });
      return data?.deleteInvite;
    },
    [deleteInviteMutation],
  );

  return { deleteInvite, loading, error };
}

const ACCEPT_INVITE = gql`
  mutation acceptInvite($teamId: String!) {
    acceptInvite(teamId: $teamId)
  }
`;

export function useAcceptInvite() {
  const [acceptInviteMutation, { loading, error }] =
    useMutation<AcceptInviteData>(ACCEPT_INVITE);

  const acceptInvite = useCallback(
    async (teamId: string) => {
      const { data } = await acceptInviteMutation({
        variables: { teamId },
      });
      return data?.acceptInvite;
    },
    [acceptInviteMutation],
  );

  return { acceptInvite, loading, error };
}

const REJECT_INVITE = gql`
  mutation rejectInvite($teamId: String!) {
    rejectInvite(teamId: $teamId)
  }
`;

export function useRejectInvite() {
  const [rejectInviteMutation, { loading, error }] =
    useMutation<RejectInviteData>(REJECT_INVITE);

  const rejectInvite = useCallback(
    async (teamId: string) => {
      const { data } = await rejectInviteMutation({
        variables: { teamId },
      });
      return data?.rejectInvite;
    },
    [rejectInviteMutation],
  );

  return { rejectInvite, loading, error };
}
